/**
 * Benchmark Runner for Browser-First AI SDK
 * Step 6: Performance measurement implementation
 *
 * TTFT Calculation:
 * - Time from calling chatStream() to receiving first 'delta' event with non-empty deltaText
 * - Uses performance.now() for high-precision timing
 *
 * Tokens/sec Calculation:
 * - outputTokens / (totalTime - ttft) for generation speed
 * - If TTFT equals totalTime (single token), uses totalTime
 *
 * Load Time:
 * - Captured via onProgress callback during model loading
 * - Only recorded if model load actually occurred in that run
 */
// === Constants ===
const DEFAULT_RUNS = 3;
const DEFAULT_WARMUP_RUNS = 1;
const DEFAULT_CONCURRENCY = 1;
// === Helper Functions ===
/**
 * Calculate median of an array of numbers.
 */
function median(values) {
    if (values.length === 0)
        return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
}
/**
 * Calculate average of an array of numbers.
 */
function average(values) {
    if (values.length === 0)
        return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
}
/**
 * Estimate token count from text using simple heuristic.
 * Rough approximation: ~4 characters per token for English text.
 * This is clearly labeled as estimated in the result.
 */
function estimateTokenCount(text) {
    // Simple heuristic: split on whitespace and punctuation patterns
    // More accurate would be to use actual tokenizer, but this is sufficient for benchmarks
    const words = text.split(/\s+/).filter(w => w.length > 0);
    // Approximate: 1 word ≈ 1.3 tokens on average for English
    return Math.ceil(words.length * 1.3);
}
/**
 * Convert BenchmarkCase to ChatMessage array.
 */
function caseToMessages(benchmarkCase) {
    if (benchmarkCase.messages && benchmarkCase.messages.length > 0) {
        return benchmarkCase.messages;
    }
    if (benchmarkCase.prompt) {
        return [
            { role: 'system', content: 'You are a helpful assistant. Follow instructions precisely.' },
            { role: 'user', content: benchmarkCase.prompt },
        ];
    }
    throw new Error(`BenchmarkCase "${benchmarkCase.name}" must have either prompt or messages`);
}
/**
 * Calculate summary statistics from run results.
 */
function calculateSummary(runs) {
    // Filter to only successful, non-warmup runs
    const timedRuns = runs.filter(r => !r.isWarmup && !r.error);
    const failedRuns = runs.filter(r => !r.isWarmup && r.error);
    if (timedRuns.length === 0) {
        return {
            successfulRuns: 0,
            failedRuns: failedRuns.length,
            avgTtftMs: 0,
            medianTtftMs: 0,
            minTtftMs: 0,
            maxTtftMs: 0,
            avgTokensPerSec: 0,
            medianTokensPerSec: 0,
            avgTotalTimeMs: 0,
            medianTotalTimeMs: 0,
            avgOutputTokens: 0,
        };
    }
    const ttfts = timedRuns.map(r => r.ttftMs);
    const tokPerSecs = timedRuns.map(r => r.tokensPerSec);
    const totalTimes = timedRuns.map(r => r.totalTimeMs);
    const outputTokens = timedRuns.map(r => r.outputTokens);
    return {
        successfulRuns: timedRuns.length,
        failedRuns: failedRuns.length,
        avgTtftMs: average(ttfts),
        medianTtftMs: median(ttfts),
        minTtftMs: Math.min(...ttfts),
        maxTtftMs: Math.max(...ttfts),
        avgTokensPerSec: average(tokPerSecs),
        medianTokensPerSec: median(tokPerSecs),
        avgTotalTimeMs: average(totalTimes),
        medianTotalTimeMs: median(totalTimes),
        avgOutputTokens: average(outputTokens),
    };
}
// === Benchmark Runner ===
/**
 * Run a single benchmark iteration.
 */
async function runSingleBenchmark(client, benchmarkCase, modelId, runIndex, isWarmup, loadTimeMs, abortAfterMs, signal) {
    const startedAt = Date.now();
    const startTime = performance.now();
    let ttftMs = 0;
    let firstTokenTime = null;
    let outputText = '';
    let outputTokens = 0;
    let outputTokensEstimated = true;
    let promptTokens;
    let completionTokens;
    let totalTokens;
    let warmStart = false;
    let engine = 'webllm';
    let error;
    // Create timeout abort controller if needed
    let timeoutId;
    const combinedController = new AbortController();
    if (abortAfterMs) {
        timeoutId = setTimeout(() => {
            combinedController.abort();
        }, abortAfterMs);
    }
    // Also abort if parent signal aborts
    if (signal) {
        signal.addEventListener('abort', () => combinedController.abort());
    }
    try {
        const messages = caseToMessages(benchmarkCase);
        const stream = client.chatStream({
            model: modelId,
            messages,
            signal: combinedController.signal,
        });
        for await (const event of stream) {
            switch (event.type) {
                case 'delta':
                    if (event.deltaText && firstTokenTime === null) {
                        firstTokenTime = performance.now();
                        ttftMs = firstTokenTime - startTime;
                    }
                    if (event.deltaText) {
                        outputText += event.deltaText;
                    }
                    // Capture warmStart and engine from delta events
                    if (event.meta) {
                        if (event.meta.warmStart !== undefined) {
                            warmStart = event.meta.warmStart;
                        }
                        engine = event.meta.engine;
                    }
                    break;
                case 'done':
                    // Capture final metadata
                    if (event.meta) {
                        if (event.meta.warmStart !== undefined) {
                            warmStart = event.meta.warmStart;
                        }
                        engine = event.meta.engine;
                    }
                    // Capture usage stats
                    if (event.usage) {
                        promptTokens = event.usage.promptTokens;
                        completionTokens = event.usage.completionTokens;
                        totalTokens = event.usage.totalTokens;
                        if (completionTokens !== undefined && completionTokens > 0) {
                            outputTokens = completionTokens;
                            outputTokensEstimated = false;
                        }
                    }
                    break;
                case 'error':
                    error = event.error;
                    // Capture metadata even on error
                    if (event.meta) {
                        if (event.meta.warmStart !== undefined) {
                            warmStart = event.meta.warmStart;
                        }
                        engine = event.meta.engine;
                    }
                    break;
            }
        }
    }
    catch (err) {
        error = {
            message: err instanceof Error ? err.message : String(err),
            code: 'EXCEPTION',
        };
    }
    finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
    const endTime = performance.now();
    const totalTimeMs = endTime - startTime;
    const completedAt = Date.now();
    // If no first token was received, TTFT equals total time
    if (firstTokenTime === null) {
        ttftMs = totalTimeMs;
    }
    // Estimate tokens if not provided by usage
    if (outputTokensEstimated && outputText.length > 0) {
        outputTokens = estimateTokenCount(outputText);
    }
    // Calculate tokens per second
    // Use time from first token to completion for generation speed
    // If TTFT >= totalTime (edge case), use totalTime
    const generationTimeMs = Math.max(totalTimeMs - ttftMs, 1); // Avoid division by zero
    const tokensPerSec = (outputTokens / generationTimeMs) * 1000;
    return {
        runIndex,
        isWarmup,
        loadTimeMs,
        ttftMs,
        totalTimeMs,
        outputTokens,
        outputTokensEstimated,
        tokensPerSec: isFinite(tokensPerSec) ? tokensPerSec : 0,
        promptTokens,
        completionTokens,
        totalTokens,
        warmStart,
        engine,
        error,
        startedAt,
        completedAt,
    };
}
/**
 * Run benchmarks for multiple cases.
 *
 * @param client - The SDK client to benchmark
 * @param cases - Array of benchmark cases to run
 * @param options - Benchmark options
 * @param onProgress - Optional progress callback
 * @returns Array of benchmark results
 */
export async function runBenchmarks(client, cases, options, onProgress, getLoadTimeMs) {
    const { model, runs = DEFAULT_RUNS, warmupRuns = DEFAULT_WARMUP_RUNS, concurrency = DEFAULT_CONCURRENCY, abortAfterMs, signal, } = options;
    // TODO (Step 7+): Implement parallel concurrency
    if (concurrency > 1) {
        console.warn('Concurrency > 1 not yet implemented, running sequentially');
    }
    const results = [];
    const totalRuns = warmupRuns + runs;
    for (let caseIndex = 0; caseIndex < cases.length; caseIndex++) {
        const benchmarkCase = cases[caseIndex];
        const caseStartedAt = Date.now();
        const runResults = [];
        // Check for abort
        if (signal?.aborted) {
            break;
        }
        for (let runIndex = 0; runIndex < totalRuns; runIndex++) {
            const isWarmup = runIndex < warmupRuns;
            // Emit progress: loading phase
            if (onProgress) {
                onProgress({
                    caseIndex,
                    totalCases: cases.length,
                    runIndex,
                    totalRuns,
                    isWarmup,
                    phase: 'loading',
                    caseName: benchmarkCase.name,
                });
            }
            // Check for abort
            if (signal?.aborted) {
                break;
            }
            // Emit progress: generating phase
            if (onProgress) {
                onProgress({
                    caseIndex,
                    totalCases: cases.length,
                    runIndex,
                    totalRuns,
                    isWarmup,
                    phase: 'generating',
                    caseName: benchmarkCase.name,
                });
            }
            // Get load time from client if available (first run only typically has load time)
            const loadTimeMs = getLoadTimeMs?.();
            const runResult = await runSingleBenchmark(client, benchmarkCase, model, runIndex, isWarmup, loadTimeMs, abortAfterMs, signal);
            runResults.push(runResult);
            // Emit progress: complete phase with result
            if (onProgress) {
                onProgress({
                    caseIndex,
                    totalCases: cases.length,
                    runIndex,
                    totalRuns,
                    isWarmup,
                    phase: 'complete',
                    caseName: benchmarkCase.name,
                    runResult,
                });
            }
        }
        const caseCompletedAt = Date.now();
        // Determine actual model ID and engine from runs
        const successfulRun = runResults.find(r => !r.error);
        const modelId = model === 'auto' ? (successfulRun?.engine ?? 'webllm') : model;
        const engine = successfulRun?.engine ?? 'webllm';
        results.push({
            caseName: benchmarkCase.name,
            modelId: model,
            engine,
            runs: runResults,
            summary: calculateSummary(runResults),
            startedAt: caseStartedAt,
            completedAt: caseCompletedAt,
        });
    }
    return results;
}
//# sourceMappingURL=runner.js.map